import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

import AddCategory from '../../components/category/add-category/add-category.component';
import Button from '../../components/reusable/button/button.component';
import CategoriesTable from '../../components/reusable/tables/categories-table/categories-table.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import Pagination from '../../components/reusable/pagination/pagination.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';

import {
    ContentContainer,
    MainContainer,
    MainTitle
} from '../../styles/page.styles';

const client = new Client();

const CategoriesPage = () => {
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [categoriesCount, setCategoriesCount] = useState(null);
    const [showAddCategory, setShowAddCategory] = useState(false);

    const {
        page,
        size,
        search,
        sortKey,
        sortDirection,
        setPage,
        setSize,
        setSearch,
        setSortKey,
        setSortDirection
    } = useTableState({ defaultSize: 25 });

    const isInitialMount = useRef(true);

    const getCategories = useCallback(async () => {
        setLoading(true);
        let query = `?page=${page}&size=${size}`;
        if (search) query += `&search=${encodeURIComponent(search)}`;
        if (sortDirection) query += `&sortDirection=${sortDirection}`;
        if (sortKey) query += `&sortKey=${sortKey}`;
        const res = await client.getAllCategories(query);
        setCategories(res.rows);
        setCategoriesCount(res.count);
        setLoading(false);
    }, [page, size, search, sortKey, sortDirection]);

    useEffect(() => {
        getCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        getCategories();
    }, [page, size, search, sortKey, sortDirection, getCategories]);

    const handleSearchSubmit = useCallback(() => {}, []);

    // Export configuration
    const exportFn = async () => {
        const res = await client.exportCategories();
        return res.rows || [];
    };

    const formatExportData = (data) => {
        return data.map((category) => {
            const createdDate = dayjs(category.createdAt).format('MM/DD/YYYY');
            return [
                category.id || '',
                category.name || '',
                category.description || '',
                category.CategoryType?.name || '',
                category.status ? 'Active' : 'Inactive',
                createdDate
            ];
        });
    };

    const exportHeaders = ['ID', 'Name', 'Description', 'Type', 'Status', 'Created Date'];

    return (
        <MainContainer>
            <ComponentModal isOpen={showAddCategory} onClose={() => setShowAddCategory(false)} title="Add Category">
                <AddCategory
                    onClose={() => setShowAddCategory(false)}
                    onSuccess={() => {
                        setShowAddCategory(false);
                        getCategories();
                    }}
                />
            </ComponentModal>

            <ContentContainer>
                <MainTitle>Categories</MainTitle>

                <TableControls
                    search={search}
                    setSearch={setSearch}
                    onSearchSubmit={handleSearchSubmit}
                    size={size}
                    setSize={setSize}
                    entityName="categories"
                    exportFn={exportFn}
                    formatExportData={formatExportData}
                    exportHeaders={exportHeaders}
                    additionalButtons={
                        <Button onClick={() => setShowAddCategory(true)}>Add Category</Button>
                    }
                />

                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        <CategoriesTable
                            categories={categories}
                            sortKey={sortKey}
                            setSortKey={setSortKey}
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                        />

                        <Pagination
                            count={categoriesCount}
                            size={size}
                            page={page}
                            changePage={setPage}
                        />
                    </>
                )}
            </ContentContainer>
        </MainContainer>
    );
};

export default CategoriesPage;
